import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Edit3, Film, UserPlus, Upload, Loader2, ShieldOff, RotateCcw, DollarSign } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Drag-and-drop / click-to-pick uploader for video or image files. */
function FileUploadField({ kind, value, onChange, label, accept }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputId = `file-${kind}-${Math.random().toString(36).slice(2, 8)}`;

  const upload = async (file) => {
    setError("");
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/upload?kind=${kind}`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      onChange(data.url);
      toast.success(`${kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (e) {
      setError(e.message || "Upload failed");
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div>
      <Label className="text-gray-300">{label}</Label>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="mt-1 rounded-lg border border-dashed border-white/20 bg-black/40 p-4 hover:border-[#FFD700]/40 transition-colors"
      >
        {uploading ? (
          <div className="flex items-center gap-3 text-sm text-gray-300">
            <Loader2 className="w-5 h-5 animate-spin text-[#FFD700]" />
            <span>Uploading… don't close this tab.</span>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              data-testid={`upload-${kind}-input`}
              id={inputId}
              type="file"
              accept={accept}
              className="hidden"
              onChange={onPick}
            />
            <label
              htmlFor={inputId}
              className="inline-flex items-center gap-2 rounded-full bg-[#FFD700] text-black px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-[#FFE873]"
            >
              <Upload className="w-4 h-4" /> Choose {kind === "video" ? "video" : "image"}…
            </label>
            <p className="text-[11px] text-gray-500">or drag & drop here</p>
            {value && (
              <p className="text-[11px] text-gray-400 truncate" title={value}>
                Current: <span className="text-gray-300">{value}</span>
              </p>
            )}
          </div>
        )}
        {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
      </div>
    </div>
  );
}

const DEFAULTS = {
  type: "movie", title: "", description: "", poster_url: "",
  video_url: "", backdrop_url: "", year: "", genre: "", duration: "", featured: false, coming_soon: false,
};

function RescueUserCard() {
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const grant = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/grant-premium", {
        email: email.trim(), days: Number(days) || 30, note,
      });
      setResult(r.data);
      toast.success(`Granted ${r.data.days_granted} days to ${r.data.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not grant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#141419] border-white/10 text-white">
      <CardContent className="p-7">
        <div className="flex items-center gap-2 text-[#FFD700] text-xs uppercase tracking-[0.3em] font-semibold mb-2">
          <UserPlus className="w-3.5 h-3.5" /> Customer rescue
        </div>
        <h2 className="font-display text-xl font-bold mb-1">Manually grant premium</h2>
        <p className="text-sm text-gray-400 mb-5">
          Activate a customer who paid on PayPal but didn't auto-unlock. Pre-creates their account if they haven't signed in yet — they'll be premium the moment they log in with Google.
        </p>

        <form onSubmit={grant} className="grid md:grid-cols-[2fr_120px_2fr_auto] gap-3 items-end">
          <div>
            <Label className="text-gray-300 text-xs">Customer email</Label>
            <Input
              data-testid="rescue-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              type="email"
              required
              className="mt-1 bg-black/40 border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Days</Label>
            <Input
              data-testid="rescue-days"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              type="number"
              min="1" max="365"
              className="mt-1 bg-black/40 border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Internal note (optional)</Label>
            <Input
              data-testid="rescue-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="PayPal sub I-XXXXX – paid 2026-02-04"
              className="mt-1 bg-black/40 border-white/20 text-white"
            />
          </div>
          <Button
            data-testid="rescue-submit"
            type="submit"
            disabled={busy}
            className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-6 font-semibold h-10"
          >
            {busy ? "Granting…" : "Grant premium"}
          </Button>
        </form>

        {result && (
          <div className="mt-5 rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/5 p-4 text-sm text-gray-200">
            ✓ Granted <strong>{result.days_granted} days</strong> to <strong>{result.email}</strong>
            {result.user_existed === false && " (account pre-created — they'll be premium on first Google sign-in)"}
            . Welcome email sent.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BillingCancellationsCard() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("Customer requested cancellation");
  const [busyCancel, setBusyCancel] = useState(false);
  const [busyReconcile, setBusyReconcile] = useState(false);
  const [busyRefund, setBusyRefund] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const forceCancel = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!window.confirm(`Force-cancel PayPal subscription for ${email}? This immediately stops future billing.`)) return;
    setBusyCancel(true); setLastResult(null);
    try {
      const r = await api.post("/admin/force-cancel", { email: email.trim(), reason });
      setLastResult({ kind: "force-cancel", data: r.data });
      toast.success(`Cancelled: ${r.data.cancel_result} (status: ${r.data.paypal_status_after_cancel || "n/a"})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Force-cancel failed");
    } finally {
      setBusyCancel(false);
    }
  };

  const reconcile = async () => {
    if (!window.confirm("Sweep ALL cancelled users and force-cancel their PayPal subscriptions if still active. Continue?")) return;
    setBusyReconcile(true); setLastResult(null);
    try {
      const r = await api.post("/admin/reconcile-cancellations", {});
      setLastResult({ kind: "reconcile", data: r.data });
      toast.success(`Checked ${r.data.checked} · Cancelled now: ${r.data.cancelled} · Already cancelled: ${r.data.already_cancelled}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reconcile failed");
    } finally {
      setBusyReconcile(false);
    }
  };

  const refundSweep = async (dry_run) => {
    const label = dry_run ? "Preview refunds (dry run)" : "Refund charges";
    if (!dry_run) {
      if (!window.confirm("This will REFUND every PayPal charge that happened AFTER a user cancelled. Money will leave your PayPal balance. Continue?")) return;
    }
    setBusyRefund(true); setLastResult(null);
    try {
      const r = await api.post("/admin/refund-cancellations", { lookback_days: 180, dry_run: !!dry_run });
      setLastResult({ kind: dry_run ? "refund-dry" : "refund", data: r.data });
      toast.success(`${label}: ${r.data.refunded_count} charge(s) · $${r.data.refunded_amount_usd}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund sweep failed");
    } finally {
      setBusyRefund(false);
    }
  };

  return (
    <Card className="bg-[#141419] border-white/10 text-white mt-6">
      <CardContent className="p-7">
        <div className="flex items-center gap-2 text-[#E50914] text-xs uppercase tracking-[0.3em] font-semibold mb-2">
          <ShieldOff className="w-3.5 h-3.5" /> Billing safety
        </div>
        <h2 className="font-display text-xl font-bold mb-1">Cancel & refund canceled users</h2>
        <p className="text-sm text-gray-400 mb-5">
          Use these to STOP PayPal billing for anyone who cancelled and REFUND any charges taken after their cancellation date.
          Always run <em>Preview refunds</em> first to see what would be refunded before issuing money back.
        </p>

        <form onSubmit={forceCancel} className="grid md:grid-cols-[2fr_2fr_auto] gap-3 items-end">
          <div>
            <Label className="text-gray-300 text-xs">Customer email to force-cancel</Label>
            <Input
              data-testid="force-cancel-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              type="email"
              className="mt-1 bg-black/40 border-white/20 text-white"
            />
          </div>
          <div>
            <Label className="text-gray-300 text-xs">Reason (audit)</Label>
            <Input
              data-testid="force-cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 bg-black/40 border-white/20 text-white"
            />
          </div>
          <Button
            data-testid="force-cancel-submit"
            type="submit"
            disabled={busyCancel}
            className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-6 font-semibold h-10"
          >
            {busyCancel ? "Cancelling…" : "Force cancel"}
          </Button>
        </form>

        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-white/5">
          <Button
            data-testid="reconcile-btn"
            onClick={reconcile}
            disabled={busyReconcile}
            variant="outline"
            className="rounded-full border-white/20 text-white hover:bg-white/10"
          >
            <RotateCcw className={`w-4 h-4 mr-2 ${busyReconcile ? "animate-spin" : ""}`} />
            {busyReconcile ? "Reconciling…" : "Reconcile ALL cancellations at PayPal"}
          </Button>

          <Button
            data-testid="refund-preview-btn"
            onClick={() => refundSweep(true)}
            disabled={busyRefund}
            variant="outline"
            className="rounded-full border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            {busyRefund ? "Working…" : "Preview refunds (dry run)"}
          </Button>

          <Button
            data-testid="refund-execute-btn"
            onClick={() => refundSweep(false)}
            disabled={busyRefund}
            className="rounded-full bg-[#FFD700] text-black hover:bg-[#FFE873] font-semibold"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            {busyRefund ? "Refunding…" : "Refund charges after cancellation"}
          </Button>
        </div>

        {lastResult && (
          <div className="mt-5 rounded-lg border border-white/10 bg-black/40 p-4 text-xs text-gray-200 max-h-72 overflow-auto">
            <div className="text-[#FFD700] uppercase tracking-wider font-semibold mb-2">
              Result — {lastResult.kind}
            </div>
            <pre className="whitespace-pre-wrap text-gray-300 text-[11px] leading-relaxed">
{JSON.stringify(lastResult.data, null, 2)}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [authed, setAuthed] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULTS);

  useEffect(() => {
    api.get("/admin/me")
      .then(() => setAuthed(true))
      .catch(() => navigate("/admin"));
  }, [navigate]);

  const load = async () => {
    const r = await api.get("/admin/content");
    setItems(r.data);
  };

  useEffect(() => { if (authed) load(); }, [authed]);

  const openCreate = () => {
    setEditing(null);
    setForm(DEFAULTS);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...DEFAULTS, ...it, year: it.year || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      year: form.year ? parseInt(form.year, 10) : null,
    };
    try {
      if (editing) {
        await api.put(`/admin/content/${editing.id}`, payload);
        toast.success("Content updated");
      } else {
        await api.post("/admin/content", payload);
        toast.success("Content added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try {
      await api.delete(`/admin/content/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch (_) {}
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("ybtv_admin_token");
    }
    navigate("/admin");
  };

  if (!authed) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Film className="w-5 h-5 text-[#E50914]" />
            <span className="font-display font-bold text-lg tracking-tight">YBTV Admin</span>
          </div>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            onClick={logout}
            className="text-gray-300 hover:text-white hover:bg-white/5"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <RescueUserCard />
        <BillingCancellationsCard />
        <div className="flex items-end justify-between flex-wrap gap-4 mt-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-2">Library</div>
            <h1 className="font-display text-4xl font-black tracking-tighter">Content</h1>
            <p className="text-gray-400 text-sm mt-1">{items.length} items</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-content-btn"
                onClick={openCreate}
                className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-6 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" /> Add content
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141419] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {editing ? "Edit content" : "Add new content"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="form-type" className="bg-black/40 border-white/20 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E1E24] border-white/10 text-white">
                        <SelectItem value="movie">Movie</SelectItem>
                        <SelectItem value="show">Show</SelectItem>
                        <SelectItem value="trailer">Trailer</SelectItem>
                        <SelectItem value="interview">Interview</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Year</Label>
                    <Input data-testid="form-year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="bg-black/40 border-white/20 text-white mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-300">Title</Label>
                  <Input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="bg-black/40 border-white/20 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">Description</Label>
                  <Textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} className="bg-black/40 border-white/20 text-white mt-1" />
                </div>
                <FileUploadField
                  kind="poster"
                  label="Upload poster image (or paste URL below)"
                  accept="image/*"
                  value={form.poster_url}
                  onChange={(url) => setForm({ ...form, poster_url: url })}
                />
                <div>
                  <Label className="text-gray-300">Poster URL</Label>
                  <Input data-testid="form-poster" value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} required placeholder="https://… or auto-filled by upload above" className="bg-black/40 border-white/20 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">Backdrop URL (optional)</Label>
                  <Input data-testid="form-backdrop" value={form.backdrop_url} onChange={(e) => setForm({ ...form, backdrop_url: e.target.value })} placeholder="https://…" className="bg-black/40 border-white/20 text-white mt-1" />
                </div>
                <FileUploadField
                  kind="video"
                  label="Upload video file — mp4, mov, webm (or paste URL below)"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                  value={form.video_url}
                  onChange={(url) => setForm({ ...form, video_url: url })}
                />
                <div>
                  <Label className="text-gray-300">Video URL (uploaded file, mp4, YouTube, or Vimeo) {form.coming_soon && <span className="text-gray-500 font-normal">— optional for Coming Soon</span>}</Label>
                  <Input data-testid="form-video" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} required={!form.coming_soon} placeholder="auto-filled by upload, or paste an external URL" className="bg-black/40 border-white/20 text-white mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300">Genre</Label>
                    <Input data-testid="form-genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className="bg-black/40 border-white/20 text-white mt-1" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Duration</Label>
                    <Input data-testid="form-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 1h 47m" className="bg-black/40 border-white/20 text-white mt-1" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input data-testid="form-featured" type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#E50914]" />
                  Featured on landing/browse hero
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input data-testid="form-coming-soon" type="checkbox" checked={!!form.coming_soon} onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })} className="accent-[#FFD700]" />
                  Coming soon (shows a gold "Coming Soon" badge instead of unlocking)
                </label>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-gray-300 hover:bg-white/5">Cancel</Button>
                  <Button data-testid="form-submit" type="submit" className="rounded-full bg-[#E50914] hover:bg-[#F40612] text-white px-6">
                    {editing ? "Save changes" : "Add content"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-10 grid gap-4">
          {items.map((it) => (
            <Card key={it.id} data-testid={`row-${it.id}`} className="bg-[#141419] border-white/10 text-white overflow-hidden">
              <CardContent className="p-0 flex flex-col md:flex-row">
                <img src={it.poster_url} alt={it.title} className="w-full md:w-32 h-44 md:h-auto object-cover" />
                <div className="flex-1 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="border-white/20 text-gray-300 capitalize">{it.type}</Badge>
                      {it.featured && <Badge className="bg-[#FFD700] text-black hover:bg-[#FFD700]">Featured</Badge>}
                      {it.coming_soon && <Badge variant="outline" className="border-[#FFD700]/40 text-[#FFD700]">Coming Soon</Badge>}
                      {it.year && <span className="text-xs text-gray-400">{it.year}</span>}
                    </div>
                    <h3 className="font-display font-bold text-lg mt-2 leading-tight">{it.title}</h3>
                    <p className="text-sm text-gray-400 mt-1 line-clamp-2 max-w-2xl">{it.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`edit-${it.id}`} variant="outline" size="sm" onClick={() => openEdit(it)} className="border-white/20 text-white hover:bg-white/10">
                      <Edit3 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button data-testid={`delete-${it.id}`} variant="outline" size="sm" onClick={() => remove(it.id)} className="border-red-900/40 text-[#E50914] hover:bg-[#E50914]/10">
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && (
            <div className="text-center py-20 text-gray-400">
              No content yet. Click <span className="text-white font-semibold">Add content</span> to upload your first.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
